import { AltArrowLeft, Bolt } from '@solar-icons/react'
import { motion } from 'framer-motion'
import type { CSSProperties } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, Navigate, Outlet, useSearchParams } from 'react-router-dom'
import { resolveAuthRedirect } from '@/modules/auth/utils/redirect'
import { useAuthStore } from '@/store/use-auth-store'

/* Decorative brand-identity textures (design exception: layered gradients have no
   Tailwind primitive) — values verbatim from the PartiuQuadra prototype hero. */
const HERO_GLOW_STYLE: CSSProperties = {
  backgroundImage:
    'radial-gradient(circle at 75% 20%, rgba(249,115,22,.4), transparent 45%), radial-gradient(circle at 20% 80%, rgba(37,99,235,.3), transparent 40%)',
}

const HERO_STRIPES_STYLE: CSSProperties = {
  backgroundImage:
    'repeating-linear-gradient(135deg, rgba(255,255,255,.03) 0 8px, transparent 8px 16px)',
}

// Product promises that hold for every venue; no invented usage numbers.
const HERO_FACTS = [
  { value: 'auth.hero.facts.clicksValue', label: 'auth.hero.facts.clicksLabel' },
  { value: 'auth.hero.facts.callsValue', label: 'auth.hero.facts.callsLabel' },
  { value: 'auth.hero.facts.feeValue', label: 'auth.hero.facts.feeLabel' },
] as const

function BrandBlock() {
  const { t } = useTranslation('play')

  return (
    <Link to="/" className="flex items-center gap-2.5">
      <span className="flex h-8.5 w-8.5 shrink-0 items-center justify-center rounded-xl bg-primary font-display text-lg font-bold text-neutral-0">
        P
      </span>
      <span className="min-w-0">
        <span className="block truncate font-display text-lg font-bold tracking-tight text-neutral-0">
          {t('brand.name')}
        </span>
        <span className="block truncate text-xs font-semibold uppercase tracking-wide text-neutral-400">
          {t('auth.hero.byline')}
        </span>
      </span>
    </Link>
  )
}

function HeroBadge() {
  const { t } = useTranslation('play')

  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-on-dark/15 px-3 py-1.5 text-xs font-bold uppercase tracking-widest text-primary-on-dark">
      <Bolt weight="Bold" className="h-3.5 w-3.5" />
      {t('auth.hero.badge')}
    </span>
  )
}

export function AuthLayout() {
  const { t } = useTranslation('play')
  const status = useAuthStore((state) => state.status)
  const [searchParams] = useSearchParams()

  if (status === 'authenticated') {
    return <Navigate to={resolveAuthRedirect(searchParams.get('redirect'))} replace />
  }

  return (
    <div className="grid min-h-dvh grid-rows-[auto_1fr] bg-bg-subtle md:grid-cols-2 md:grid-rows-1">
      <aside className="relative hidden overflow-hidden bg-neutral-950 md:flex">
        <div className="absolute inset-0" style={HERO_GLOW_STYLE} />
        <div className="absolute inset-0" style={HERO_STRIPES_STYLE} />

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative flex w-full flex-col justify-between gap-10 p-10 lg:px-15 lg:pb-10 lg:pt-15"
        >
          <div className="border-b border-neutral-800 pb-5">
            <BrandBlock />
          </div>

          <div>
            <HeroBadge />
            <h1 className="mb-4 mt-5 font-display text-4xl font-bold leading-none tracking-tighter text-neutral-0 lg:text-5xl">
              {t('auth.hero.title')}
              <br />
              <span className="text-primary-on-dark">{t('auth.hero.titleHighlight')}</span>
            </h1>
            <p className="max-w-105 text-sm leading-relaxed text-neutral-400">
              {t('auth.hero.subtitle')}
            </p>

            <div className="mt-8 flex gap-5">
              {HERO_FACTS.map((fact) => (
                <div key={fact.label}>
                  <p className="font-display text-2xl font-bold text-neutral-0">{t(fact.value)}</p>
                  <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">
                    {t(fact.label)}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <p className="font-mono text-xs uppercase tracking-wider text-neutral-600">
            {t('auth.hero.copyright', { year: new Date().getFullYear() })}
          </p>
        </motion.div>
      </aside>

      <div className="relative overflow-hidden rounded-b-3xl bg-neutral-950 md:hidden">
        <div className="absolute inset-0" style={HERO_GLOW_STYLE} />
        <div className="absolute inset-0" style={HERO_STRIPES_STYLE} />
        <div className="relative flex items-center justify-between gap-3 px-5 pb-5 pt-4">
          <BrandBlock />
          <HeroBadge />
        </div>
      </div>

      <section className="relative flex flex-col overflow-y-auto px-5 py-6 md:px-10 md:py-10 lg:p-15">
        <Link
          to="/"
          className="absolute left-5 top-6 inline-flex w-fit items-center gap-1.5 text-xs font-medium text-text-tertiary transition-colors hover:text-text-primary md:left-10 md:top-10 lg:left-15 lg:top-15"
        >
          <AltArrowLeft className="h-3.5 w-3.5" />
          {t('auth.hero.explore')}
        </Link>

        <div className="flex w-full flex-1 items-center justify-center py-14">
          <div className="w-full max-w-md">
            {status === 'loading' ? (
              <div className="flex min-h-60 items-center justify-center">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              </div>
            ) : (
              <Outlet />
            )}
          </div>
        </div>
      </section>
    </div>
  )
}
